// Supported currencies (ISO 4217 codes)
export const SUPPORTED_CURRENCIES = ["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "CHF", "CNY", "INR", "MXN", "RON"] as const;
export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number];

// Default base currency (user's home currency for aggregations)
export const DEFAULT_BASE_CURRENCY = "USD";

/**
 * Format amount with proper currency symbol and locale
 * Uses Intl.NumberFormat for proper i18n support
 */
export function formatCurrency(amount: number, currency: string = DEFAULT_BASE_CURRENCY): string {
    // Get locale based on currency (simplified - can be enhanced with user preference)
    const localeMap: Record<string, string> = {
        USD: "en-US",
        EUR: "de-DE",
        GBP: "en-GB",
        JPY: "ja-JP",
        CAD: "en-CA",
        AUD: "en-AU",
        CHF: "de-CH",
        CNY: "zh-CN",
        INR: "en-IN",
        MXN: "es-MX",
        RON: "ro-RO",
    };

    const locale = localeMap[currency] || "en-US";

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: currency === "JPY" ? 0 : 2, // JPY has no decimals
        maximumFractionDigits: currency === "JPY" ? 0 : 2,
    }).format(amount);
}
/**
 * Get currency symbol only (for compact displays)
 */
export function getCurrencySymbol(currency: string = DEFAULT_BASE_CURRENCY): string {
    const symbols: Record<string, string> = {
        USD: "$",
        EUR: "€",
        GBP: "£",
        JPY: "¥",
        CAD: "CA$",
        AUD: "A$",
        CHF: "CHF",
        CNY: "¥",
        INR: "₹",
        MXN: "MX$",
        RON: "lei",
    };
    return symbols[currency] || currency;
}

/**
 * Mock exchange rates (In production, fetch from API like exchangerate-api.com)
 * Rates are relative to USD as base
 */
const MOCK_EXCHANGE_RATES: Record<string, number> = {
    USD: 1.0,
    EUR: 0.92,
    GBP: 0.79,
    JPY: 149.50,
    CAD: 1.36,
    AUD: 1.53,
    CHF: 0.88,
    CNY: 7.24,
    INR: 83.12,
    MXN: 17.15,
    RON: 4.60,
};

/**
 * Convert amount from one currency to another
 * @param amount - The amount to convert
 * @param fromCurrency - Source currency (ISO 4217)
 * @param toCurrency - Target currency (ISO 4217)
 * @returns Converted amount
 */
export function convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string = DEFAULT_BASE_CURRENCY
): number {
    if (fromCurrency === toCurrency) return amount;

    // Convert to USD first, then to target currency
    const amountInUSD = amount / MOCK_EXCHANGE_RATES[fromCurrency];
    const convertedAmount = amountInUSD * MOCK_EXCHANGE_RATES[toCurrency];

    return convertedAmount;
}

/**
 * Convert amount to base currency and format
 * Useful for displaying totals in a single currency
 */
export function formatInBaseCurrency(amount: number, fromCurrency: string, baseCurrency: string = DEFAULT_BASE_CURRENCY): string {
    const converted = convertCurrency(amount, fromCurrency, baseCurrency);
    return formatCurrency(converted, baseCurrency);
}

/**
 * Get exchange rate between two currencies
 */
export function getExchangeRate(fromCurrency: string, toCurrency: string): number {
    if (fromCurrency === toCurrency) return 1.0;
    return MOCK_EXCHANGE_RATES[toCurrency] / MOCK_EXCHANGE_RATES[fromCurrency];
}
